import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import MapActiveFilterChips from './MapActiveFilterChips';
import './MapControls.css';

/** Ikona GPS / celownik — currentColor z przycisku */
function MapLocateMeIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="12" r="7.5" stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.35" strokeDasharray="3 3" />
      <path d="M12 4v3M12 17v3M4 12h3M17 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="12" r="2.75" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/** Wejście w pełny ekran — cztery rogi „rozchylone” */
function MapFullscreenEnterIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M9 3H3v6M15 3h6v6M9 21H3v-6M15 21h6v-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Wyjście — rogi „ściśnięte” */
function MapFullscreenExitIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Baner „wydarzenia → lista”: karta z wierszami + strzałka w prawo (currentColor).
 */
function MapStatusListIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="3.75" y="4.75" width="11.75" height="14.5" rx="2.35" stroke="currentColor" strokeWidth="1.45" />
      <path
        d="M6.85 9.6h5.55M6.85 12.35h5.55M6.85 15.1h4"
        stroke="currentColor"
        strokeWidth="1.32"
        strokeLinecap="round"
      />
      <path
        d="M17.15 9.1L20.45 12l-3.3 2.9"
        stroke="currentColor"
        strokeWidth="1.48"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Pinezka — wariant „pusty obszar” */
function MapStatusPinIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M12 21.5c0 0 6.5-4.2 6.5-11.2a6.5 6.5 0 1 0-13 0C5.5 17.3 12 21.5 12 21.5z"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10.25" r="2.2" fill="currentColor" fillOpacity="0.22" stroke="none" />
    </svg>
  );
}

function MapControls({
  onLocateMe,
  /** Drugie kliknięcie „Zlokalizuj mnie” przywraca widok — zmienia etykietę przycisku */
  locateUndoAvailable = false,
  mapFullscreen = false,
  onToggleMapFullscreen,
  visibleEvents,
  onShowList,
  loading,
  error,
  totalEvents = 0,
  /** Gdy ustawione (np. filtr okręgu wyszukiwania), licznik „/ X” używa tej wartości zamiast wszystkich wydarzeń */
  filteredTotalEvents = null,
  emptyHintDismissed = false,
  onDismissEmptyHint,
  /** Aktywne filtry mapy — chipy z osobnym usuwaniem (pod banerem) */
  activeFilterChips = null,
  onDismissActiveFilter = null,
}) {
  const { t } = useTranslation(['discover']);
  const foundCount = Array.isArray(visibleEvents) ? visibleEvents.length : 0;
  const showResultsBadge = !loading && !error;
  const badgeTotal =
    typeof filteredTotalEvents === 'number' && filteredTotalEvents >= 0 ? filteredTotalEvents : totalEvents;

  // Pokaż hint gdy na widocznym obszarze nie ma żadnych pinezek
  const showEmptyHint = !loading && !error && visibleEvents.length === 0 && !emptyHintDismissed;
  
  // Czy kategoria jest całkowicie pusta?
  const isCategoryEmpty = totalEvents === 0;
  const noEventsInSearchCircle =
    typeof filteredTotalEvents === 'number' && filteredTotalEvents === 0 && totalEvents > 0;
  
  // Pokaż przycisk listy gdy są widoczne wydarzenia
  const showListButton = !loading && !error && visibleEvents.length > 0;

  const hasActiveChips = Array.isArray(activeFilterChips) && activeFilterChips.length > 0;
  const showStatusStack = showEmptyHint || showListButton || hasActiveChips;

  // Tekst w zależności od sytuacji
  const getEmptyHintText = () => {
    if (isCategoryEmpty) {
      return 'Brak wydarzeń w tej kategorii';
    }
    if (noEventsInSearchCircle) {
      return t(
        'discover.map.noEventsInSearchCircle',
        'Brak wydarzeń w wybranym promieniu wyszukiwania — powiększ zasięg lub wybierz inne miejsce.'
      );
    }
    return 'Brak wydarzeń w tym obszarze';
  };

  const getEmptyHintAction = () => {
    if (isCategoryEmpty) {
      return 'Zmień kategorię';
    }
    if (noEventsInSearchCircle) {
      return t('discover.map.adjustSearchOrMap', 'Zmień wyszukiwanie lub mapę');
    }
    return 'Przesuń lub oddal mapę';
  };

  return (
    <>
      {showResultsBadge && (
        <div className="map-results-status-badge" role="status" aria-live="polite">
          <span className="map-results-status-label">
            {t('discover.map.foundShort', 'Znalezione')}:
          </span>
          <strong className="map-results-status-value">{foundCount}</strong>
          {badgeTotal > foundCount && badgeTotal > 0 && (
            <span className="map-results-status-total">/ {badgeTotal}</span>
          )}
        </div>
      )}

      <button
        className={`map-center-user-button map-center-user-button--stacked${locateUndoAvailable ? ' map-center-user-button--locate-undo' : ''}`}
        onClick={onLocateMe}
        title={
          locateUndoAvailable
            ? t('discover.map.locateMeUndoTitle', 'Przywróć poprzedni widok mapy')
            : t('discover.map.locateMeTitle', 'Pobierz lub pokaż moją lokalizację na mapie')
        }
        type="button"
        aria-label={
          locateUndoAvailable
            ? t('discover.map.locateMeUndoAria', 'Przywróć widok sprzed lokalizacji')
            : t('discover.map.locateMe', 'Zlokalizuj mnie')
        }
      >
        <MapLocateMeIcon className="map-center-user-icon-svg" />
      </button>

      {typeof onToggleMapFullscreen === 'function' && (
        <button
          className="map-fullscreen-button map-fullscreen-button--stacked"
          onClick={onToggleMapFullscreen}
          title={
            mapFullscreen
              ? t('discover.map.exitFullscreenTitle', 'Wyjdź z trybu pełnoekranowego mapy')
              : t('discover.map.fullscreenTitle', 'Włącz mapę na pełnym ekranie')
          }
          type="button"
          aria-label={
            mapFullscreen
              ? t('discover.map.exitFullscreen', 'Zamknij pełny ekran')
              : t('discover.map.fullscreen', 'Pełny ekran')
          }
          aria-pressed={mapFullscreen}
        >
          {mapFullscreen ? (
            <MapFullscreenExitIcon className="map-fullscreen-icon-svg" />
          ) : (
            <MapFullscreenEnterIcon className="map-fullscreen-icon-svg" />
          )}
        </button>
      )}

      {showStatusStack && (
        <div className="map-status-stack">
          {/* Empty state hint - no pins visible on current map view */}
          {showEmptyHint && (
            <div className="map-status-bar map-status-bar--empty">
              <span className="map-status-icon map-status-icon--symbolic map-status-icon--empty" aria-hidden="true">
                <MapStatusPinIcon className="map-status-icon-svg" />
              </span>
              <div className="map-status-content">
                <div className="map-status-primary-row">
                  <span className="map-status-text">{getEmptyHintText()}</span>
                  <span className="map-status-hint">{getEmptyHintAction()}</span>
                </div>
              </div>
              {onDismissEmptyHint && (
                <button
                  type="button"
                  className="map-status-close"
                  onClick={onDismissEmptyHint}
                  aria-label="Zamknij"
                >
                  ✕
                </button>
              )}
            </div>
          )}

          {/* Show visible events as list button */}
          {showListButton && (
            <button className="map-status-bar map-status-bar--events" onClick={onShowList} type="button">
              <span className="map-status-icon map-status-icon--symbolic" aria-hidden="true">
                <MapStatusListIcon className="map-status-icon-svg" />
              </span>
              <div className="map-status-content">
                <div className="map-status-title-row map-status-title-row--events">
                  <span className="map-status-text">
                    {visibleEvents.length === 1 ? (
                      <Trans
                        i18nKey="discover.map.foundEvent"
                        components={{
                          num: <span className="map-status-count" key={`c-${visibleEvents.length}`} />,
                        }}
                      />
                    ) : visibleEvents.length < 5 ? (
                      <Trans
                        i18nKey="discover.map.foundEventsFew"
                        values={{ count: visibleEvents.length }}
                        components={{
                          num: <span className="map-status-count" key={`c-${visibleEvents.length}`} />,
                        }}
                      />
                    ) : (
                      <Trans
                        i18nKey="discover.map.foundEvents"
                        values={{ count: visibleEvents.length }}
                        components={{
                          num: <span className="map-status-count" key={`c-${visibleEvents.length}`} />,
                        }}
                      />
                    )}
                  </span>
                </div>
                <div className="map-status-hint-wrap map-status-hint-wrap--events">
                  <span className="map-status-hint">
                    {t('discover.map.showAsList', 'Wyświetl jako listę')}
                  </span>
                </div>
              </div>
            </button>
          )}

          {hasActiveChips && (
            <MapActiveFilterChips chips={activeFilterChips} onDismiss={onDismissActiveFilter} />
          )}
        </div>
      )}
    </>
  );
}

export default MapControls;
